import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import CompanyLogoSvg, {
    COMPANY_LOGO_FILE_PNG,
    COMPANY_LOGO_FILE_SVG,
    COMPANY_LOGO_SIZE,
} from "./CompanyLogoSvg";

const SAVE_BAR_H = 72;

function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Company logo capture page — 512×512 studio mark + SVG/PNG download.
 */
export default function CompanyLogoPage() {
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / COMPANY_LOGO_SIZE, availableH / COMPANY_LOGO_SIZE),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleDownloadSvg = useCallback(() => {
        const node = frameRef.current?.querySelector("svg");
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const serialized = new XMLSerializer().serializeToString(node);
            const blob = new Blob([serialized], {
                type: "image/svg+xml;charset=utf-8",
            });
            downloadBlob(blob, COMPANY_LOGO_FILE_SVG);
        } catch (e) {
            setSaveError(e?.message || "Failed to download SVG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    const handleDownloadPng = useCallback(async () => {
        const node = frameRef.current?.querySelector("svg");
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const serialized = new XMLSerializer().serializeToString(node);
            const svgBlob = new Blob([serialized], {
                type: "image/svg+xml;charset=utf-8",
            });
            const url = URL.createObjectURL(svgBlob);
            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => reject(new Error("Failed to render SVG"));
                img.src = url;
            });
            const canvas = document.createElement("canvas");
            canvas.width = COMPANY_LOGO_SIZE;
            canvas.height = COMPANY_LOGO_SIZE;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FCFCFD";
            ctx.fillRect(0, 0, COMPANY_LOGO_SIZE, COMPANY_LOGO_SIZE);
            ctx.drawImage(img, 0, 0, COMPANY_LOGO_SIZE, COMPANY_LOGO_SIZE);
            URL.revokeObjectURL(url);
            await new Promise((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("PNG export failed"));
                            return;
                        }
                        downloadBlob(blob, COMPANY_LOGO_FILE_PNG);
                        resolve();
                    },
                    "image/png",
                    1,
                );
            });
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F1F5F9",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(COMPANY_LOGO_SIZE * previewScale),
                    height: Math.round(COMPANY_LOGO_SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 8px 32px rgba(15, 23, 42, 0.12)",
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="company-logo"
                    sx={{
                        width: COMPANY_LOGO_SIZE,
                        height: COMPANY_LOGO_SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        lineHeight: 0,
                    }}
                >
                    <CompanyLogoSvg />
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    minHeight: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    py: 1,
                    bgcolor: "rgba(255, 255, 255, 0.96)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.35)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                    Company logo · {COMPANY_LOGO_SIZE}×{COMPANY_LOGO_SIZE}
                </Typography>
                <Stack direction="row" spacing={1.25}>
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveAltIcon />
                            )
                        }
                        onClick={handleDownloadSvg}
                        disabled={saving}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                        Download SVG
                    </Button>
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveAltIcon />
                            )
                        }
                        onClick={handleDownloadPng}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: "#0ea5e9",
                            "&:hover": { bgcolor: "#0284c7" },
                        }}
                    >
                        Download PNG
                    </Button>
                </Stack>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
